<template>
  <modal v-bind="$attrs" v-on="$listeners" :errors="errors">
    <template v-slot:title>
      <h1>
        {{
          roleAdmin == "associate_admin"
            ? $t($i18nLabelsHelper(currentOrganizationType, "get_set_up.step1.title"))
            : "Invite Admin"
        }}
      </h1>
      <p>
        {{ roleAdmin == "associate_admin" ? $t("organization_profile.invite_admin_modal.invite_admin_desc") : "" }}
      </p>
    </template>
    <template v-slot:body>
      <el-form class="auth-form" :model="form" :rules="rules" ref="form">
        <div class="form-row">
          <elsa-input
            name="first_name"
            :label="$t('organization_profile.invite_admin_modal.first_name')"
            class="first-name"
            :placeholder="$t('organization_profile.invite_admin_modal.first_name')"
            v-model.lazy="form.first_name"
          >
            {{ $t("organization_profile.edit_organization_modal.city_field.label") }}
          </elsa-input>
          <elsa-input
            name="last_name"
            :label="$t('organization_profile.invite_admin_modal.last_name')"
            class="last-name"
            :placeholder="$t('organization_profile.invite_admin_modal.last_name')"
            v-model.lazy="form.last_name"
          >
            {{ $t("organization_profile.edit_organization_modal.state_field.label") }}
          </elsa-input>
        </div>
        <elsa-input
          name="email"
          :label="$t('organization_profile.invite_admin_modal.email_field.label')"
          :placeholder="$t('organization_profile.invite_admin_modal.email_field.placeholder')"
          v-model.trim.lazy="form.email"
        >
          {{ $t("organization_profile.invite_admin_modal.email_field.label") }}
        </elsa-input>
      </el-form>
    </template>
    <template v-slot:footer>
      <span class="dialog-footer">
        <tertiary-button @click.native="handleCancelClick">
          {{ $t("organization_profile.invite_admin_modal.cancel_button") }}
        </tertiary-button>
        <primary-button
          disabled
          @click.native="handleInvite"
          ref="submit"
          type="submit"
          v-loading.fullscreen.lock="fullscreenLoading"
        >
          {{ $t("organization_profile.invite_admin_modal.invite_button") }}
        </primary-button>
      </span>
    </template>
  </modal>
</template>

<script>
import Modal from "@/components/common/Modal";
import PrimaryButton from "@/components/common/buttons/PrimaryButton";
import TertiaryButton from "@/components/common/buttons/TertiaryButton";
import ElsaInput from "@/components/common/Input";
import { handleSubmitButtonState, isPropEmpty, handleFormValidation } from "@/helpers/formValidationHelper";
import { compose, isNil, of, pathOr, reject } from "ramda";
import { successMessage, warningMessage } from "@/utils/messages";
import { mapGetters } from "vuex";

export default {
  components: {
    Modal,
    PrimaryButton,
    TertiaryButton,
    ElsaInput,
  },
  props: {
    organizationId: {
      type: Number,
    },
    roleAdmin: {
      type: String,
      default: "associate_admin",
    },
  },
  data() {
    return {
      errors: {},
      form: {},
      rules: {
        email: [
          {
            type: "email",
            message: this.$t("organization_profile.invite_admin_modal.email_validation_msg"),
            trigger: "blur",
            required: true,
          },
        ],
        first_name: [
          {
            type: "string",
            message: this.$t("organization_profile.invite_admin_modal.name_validation_msg"),
            trigger: "blur",
            required: true,
          },
        ],
        last_name: [
          {
            type: "string",
            message: this.$t("organization_profile.invite_admin_modal.last_name_validation_msg"),
            trigger: "blur",
            required: true,
          },
        ],
      },
      fullscreenLoading: false,
    };
  },
  methods: {
    handleInvite(e) {
      e.preventDefault();
      handleFormValidation(this.$refs.form, this.inviteUsers, this.handleError);
    },
    async inviteUsers() {
      this.fullscreenLoading = true;
      const role = this.roleAdmin;
      const emails = compose(of, pathOr({}, ["form", "email"]))(this);
      const first_names = compose(of, pathOr({}, ["form", "first_name"]))(this);
      const last_names = compose(of, pathOr({}, ["form", "last_name"]))(this);
      const organizationId = this.organizationId;

      const payload = reject(isNil)({
        organization: organizationId,
        group: null,
        role,
        emails,
        first_names,
        last_names,
        auto_join: 1,
      });

      try {
        await this.$axios.post("/v2/accounts/invite/", payload);
        setTimeout(() => this.$store.dispatch("organization/getCurrentOrganizationAdmins"), 1000);
        this.fullscreenLoading = false;
        successMessage(this.$t("organization_profile.invite_admin_modal.invite_success"))(this);
        this.resetModal();
        this.$emit("update-pending");
        try {
          this.$gtag.event(`${document.title}-admin-invite`, {
            category: `Send-Invite`,
            label: `Add Admin`,
          });
        } catch (error) {
          error;
        }
      } catch (error) {
        const message = error.response.data.detail || error.response;
        warningMessage(message)(this);
        this.fullscreenLoading = false;
        this.resetModal();
      }
    },
    handleCancelClick() {
      this.resetModal();
    },
    onFormChange() {
      handleSubmitButtonState(this.$refs.submit.$el, !isPropEmpty("email")(this.form));
    },
    handleError(error) {
      this.errors = error;
    },
    resetModal() {
      this.errors = {};
      this.$refs.form.resetFields();
      handleSubmitButtonState(this.$refs.submit.$el, true);
      this.$emit("closed");
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        return this.onFormChange();
      },
    },
  },
  computed: {
    ...mapGetters("organization", ["isOrganizationAdmin", "isOrganizationAssociateAdmin", "currentOrganizationType"]),
  },
};
</script>

<style lang="scss" scoped>
.elsa-flat-button.el-button {
  padding: 0;
  margin: 0;
}

::v-deep .el-form .el-form-item .el-form-item__label {
  text-transform: capitalize;
}

.invite-desc {
  font-size: 16px;
  line-height: 24px;
  font-weight: normal;
  color: #121131;
  opacity: 0.8;
}

.auth-form {
  .form-row {
    display: flex;

    .first-name,
    .last-name {
      width: 50%;
    }

    .first-name {
      margin-right: 30px;
    }
  }
}
</style>
